import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  Button,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Help as HelpIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { autoWebViewJs, isRealInAndroid } from '@droid/android';
import { trackingPrint } from '@app/model';

interface Permission {
  id: string;
  name: string;
  type: 'runtime' | 'special' | 'vendor';
  status: 'granted' | 'denied' | 'unknown';
  canDetect: boolean;
  description: string;
  settingsAction: string;
}

interface PermissionsData {
  permissions: Permission[];
  timestamp: number;
  deviceBrand: string;
  apiLevel: number;
  error?: string;
}

export const PermissionStatusPanel: React.FC = () => {
  const [permissionsData, setPermissionsData] = useState<PermissionsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null);

  const fetchPermissions = async () => {
    try {
      if(!isRealInAndroid) return;
      setLoading(true);
      setError(null);
      
      // 权限刷新埋点
      trackingPrint({params:{
        type:"用户操作",
        title:"权限检查",
        subtitle:"权限刷新-开始",
        extra:{
          timestamp: Date.now(),
          isAndroid: isRealInAndroid
        }
      }});
      
      const script = `
        var info = com.fanfanlo.emergencycall.manager.PermissionJSInterface.getAllPermissionsStatus();
        info;
      `;
      
      const { javaResultString } = autoWebViewJs.callScript(script);
      
      if (!javaResultString) {
        throw new Error('No response from permission interface');
      }
      
      const data = JSON.parse(javaResultString) as PermissionsData;
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setPermissionsData(data);
      console.log('Permissions data loaded:', data);
      
      // 权限加载成功埋点
      trackingPrint({params:{
        type:"系统事件",
        title:"权限检查",
        subtitle:"权限加载-成功",
        extra:{
          timestamp: Date.now(),
          permissionsCount: data.permissions.length,
          grantedCount: data.permissions.filter(p => p.status === 'granted').length,
          deniedCount: data.permissions.filter(p => p.status === 'denied').length,
          unknownCount: data.permissions.filter(p => p.status === 'unknown').length,
          deviceBrand: data.deviceBrand,
          apiLevel: data.apiLevel
        }
      }});
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching permissions:', err);
      
      // 权限加载失败埋点
      trackingPrint({params:{
        type:"系统异常",
        title:"权限检查",
        subtitle:"权限加载-失败",
        extra:{
          timestamp: Date.now(),
          error: errorMessage,
          isAndroid: isRealInAndroid
        }
      }});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const handleOpenSettings = (permission: Permission) => {
    try {
      // 权限设置按钮点击埋点
      trackingPrint({params:{
        type:"用户操作",
        title:"权限设置",
        subtitle:"设置按钮-点击",
        extra:{
          timestamp: Date.now(),
          permissionId: permission.id,
          permissionName: permission.name,
          permissionType: permission.type,
          currentStatus: permission.status,
          canDetect: permission.canDetect
        }
      }});
      
      const script = `
        com.fanfanlo.emergencycall.manager.PermissionJSInterface.openPermissionSettings("${permission.id}");
      `;
      autoWebViewJs.callScript(script);
      console.log(`Opening settings for permission: ${permission.id}`);
      
      // 如果是消息推送权限，用户从设置页面返回后自动检查JPush状态
      if (permission.id === 'notification') {
        // 延迟执行，给用户时间完成权限设置
        setTimeout(() => {
          try {
            const jpushScript = `
              com.fanfanlo.emergencycall.jpush.JpushManager.checkAndReinitializeIfNeeded();
            `;
            autoWebViewJs.callScript(jpushScript);
            console.log('JPush reinitialization check triggered');
            
            // 刷新权限状态
            setTimeout(() => fetchPermissions(), 1000);
          } catch (jpushErr) {
            console.error('Error checking JPush status:', jpushErr);
            
            // JPush重新初始化失败埋点
            trackingPrint({params:{
              type:"系统异常",
              title:"JPush重初始化",
              subtitle:"JPush-重初始化失败",
              extra:{
                timestamp: Date.now(),
                error: jpushErr instanceof Error ? jpushErr.message : String(jpushErr)
              }
            }});
          }
        }, 3000); // 3秒后检查
      }
    } catch (err) {
      console.error('Error opening permission settings:', err);
      
      // 权限设置打开失败埋点
      trackingPrint({params:{
        type:"系统异常",
        title:"权限设置",
        subtitle:"设置打开-失败",
        extra:{
          timestamp: Date.now(),
          permissionId: permission.id,
          error: err instanceof Error ? err.message : String(err)
        }
      }});
    }
  };

  // const handleConfirmLockScreenPermission = () => {
  //   try {
  //     // 锁屏权限确认埋点
  //     trackingPrint({params:{
  //       type:"用户操作",
  //       title:"锁屏权限",
  //       subtitle:"权限确认-点击",
  //       extra:{
  //         timestamp: Date.now()
  //       }
  //     }});
      
  //     const script = `
  //       com.fanfanlo.emergencycall.manager.PermissionJSInterface.confirmLockScreenPermission();
  //     `;
  //     autoWebViewJs.callScript(script);
  //     console.log('Confirmed lock screen permission');
      
  //     // 刷新权限状态
  //     setTimeout(() => fetchPermissions(), 500);
  //   } catch (err) {
  //     console.error('Error confirming lock screen permission:', err);
      
  //     // 锁屏权限确认失败埋点
  //     trackingPrint({params:{
  //       type:"系统异常",
  //       title:"锁屏权限",
  //       subtitle:"权限确认-失败",
  //       extra:{
  //         timestamp: Date.now(),
  //         error: err instanceof Error ? err.message : String(err)
  //       }
  //     }});
  //   }
  // };

  const handleShowPermissionInfo = (permission: Permission) => {
    // 权限信息查看埋点
    trackingPrint({params:{
      type:"用户操作",
      title:"权限信息",
      subtitle:"信息按钮-点击",
      extra:{
        timestamp: Date.now(),
        permissionId: permission.id,
        permissionName: permission.name,
        permissionStatus: permission.status
      }
    }});
    
    setSelectedPermission(permission);
    setInfoDialogOpen(true);
  };

  const handleCloseInfoDialog = () => {
    // 权限信息对话框关闭埋点
    trackingPrint({params:{
      type:"模态框操作",
      title:"权限信息",
      subtitle:"信息对话框-关闭",
      extra:{
        timestamp: Date.now(),
        permissionId: selectedPermission?.id
      }
    }});
    
    setInfoDialogOpen(false);
    setSelectedPermission(null);
  };

  const getPermissionReason = (permission: Permission): string => {
    const reasonMap: Record<string, string> = {
      'android.permission.ACCESS_FINE_LOCATION': '精确定位权限用于在紧急情况下获取您的准确位置信息，以便救援人员能够快速找到您。这对于户外活动、老人独居等场景下的紧急救助至关重要。',
      'android.permission.ACCESS_COARSE_LOCATION': '大概位置权限作为精确定位的备用选择，在精确定位不可用时仍能提供基本的位置信息，确保紧急情况下不会完全失去位置服务。',
      'android.permission.BODY_SENSORS': '身体传感器权限用于访问手机的加速度计、陀螺仪等传感器，实现跌倒检测功能。当检测到您可能发生跌倒时，系统会自动触发紧急呼叫，对老人和高危工作者尤为重要。',
      'android.permission.CAMERA': '相机权限用于在紧急情况下自动拍摄现场照片，为救援人员和家属提供重要的环境信息，帮助判断紧急情况的严重程度和选择合适的救援方案。',
      'android.permission.RECORD_AUDIO': '录音权限用于记录紧急情况发生时的环境声音，可以为救援人员提供重要线索，同时支持语音紧急呼叫功能，让您在无法操作手机时也能求救。',
      'android.permission.ACTIVITY_RECOGNITION': '身体活动识别权限用于识别您当前的活动状态(如走路、跑步、静止等)，结合传感器数据提高跌倒检测的准确性，减少误报的同时确保真正的紧急情况不会被遗漏。',
      'overlay': '悬浮窗权限允许应用在任何界面上显示紧急呼叫窗口，确保即使您在使用其他应用时，紧急呼叫功能也能立即激活，不会被其他应用界面阻挡。',
      'lock_screen_popup': '后台弹出界面权限确保应用在后台运行时仍能在检测到紧急情况时立即弹出呼叫界面，即使手机处于息屏或锁定状态也不会影响紧急功能的响应速度。',
      'lock_screen_display': '锁屏显示权限允许应用在手机锁屏状态下显示紧急信息和呼叫界面，确保紧急情况发生时不会因为屏幕锁定而延误救援时机。这对于意外昏迷等无法解锁手机的情况尤为重要。',
      'desktop_shortcut': '桌面快捷方式权限允许应用在桌面创建一键紧急呼叫的快捷图标，让您在紧急情况下能够更快速地启动求救功能，特别适合老年用户或紧急情况下的快速操作。',
      'notification': '消息推送权限用于接收紧急呼叫、跌倒检测警报、系统通知等重要消息。这是确保您能及时收到紧急信息和家人求救通知的关键权限，对于紧急救援场景至关重要。'
    };
    return reasonMap[permission.id] || '该权限对于应用的正常运行是必要的，请根据您的需要进行授权。';
  };

  const getPermissionImpact = (permission: Permission): string => {
    const impactMap: Record<string, string> = {
      'android.permission.ACCESS_FINE_LOCATION': '🚨 高影响：没有位置权限将无法在紧急情况下提供准确的位置信息给救援人员',
      'android.permission.ACCESS_COARSE_LOCATION': '⚠️ 中影响：作为位置服务的备用选择，建议配合精确位置一起授权',
      'android.permission.BODY_SENSORS': '🚨 高影响：没有此权限将无法实现自动跌倒检测功能',
      'android.permission.CAMERA': '⚠️ 中影响：无法自动拍摄现场照片，但不影响基本呼叫功能',
      'android.permission.RECORD_AUDIO': '⚠️ 中影响：无法录制环境音或使用语音功能，但不影响基本呼叫功能',
      'android.permission.ACTIVITY_RECOGNITION': '⚠️ 中影响：跌倒检测准确性可能降低，增加误报或漏报风险',
      'overlay': '🚨 高影响：无法在其他应用界面上显示紧急呼叫，可能错过紧急救援时机',
      'lock_screen_popup': '🚨 高影响：手机锁屏时无法自动弹出紧急呼叫界面',
      'lock_screen_display': '🚨 高影响：锁屏状态下无法显示紧急信息，可能延误救援',
      'desktop_shortcut': '💡 低影响：无法创建桌面快捷方式，但不影响应用内的紧急呼叫功能',
      'notification': '🚨 高影响：无法接收紧急推送消息，可能错过家人求救信号或重要安全警报'
    };
    return impactMap[permission.id] || '该权限的具体影响取决于您的使用场景。';
  };

  const getStatusIcon = (permission: Permission) => {
    const iconProps = { fontSize: 18 };
    switch (permission.status) {
      case 'granted':
        return <CheckCircleIcon sx={{ color: 'success.main', ...iconProps }} />;
      case 'denied':
        return <ErrorIcon sx={{ color: 'error.main', ...iconProps }} />;
      case 'unknown':
        return <HelpIcon sx={{ color: 'warning.main', ...iconProps }} />;
      default:
        return <HelpIcon sx={{ color: 'grey.500', ...iconProps }} />;
    }
  };

  const getStatusChip = (permission: Permission) => {
    const statusConfig = {
      granted: { label: '✓', color: 'success' as const },
      denied: { label: '✗', color: 'error' as const },
      unknown: { label: '?', color: 'warning' as const }
    };

    const config = statusConfig[permission.status];
    return (
      <Chip 
        label={config.label} 
        color={config.color} 
        size="small"
        sx={{ 
          minWidth: 24,
          height: 20,
          fontSize: '0.7rem',
          '& .MuiChip-label': { px: 0.5 }
        }}
      />
    );
  };

  const getTypeLabel = (type: string) => {
    const typeLabels = {
      runtime: '运行时',
      special: '特殊',
      vendor: '厂商'
    };
    return typeLabels[type as keyof typeof typeLabels] || type;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
        <CircularProgress size={20} />
        <Typography variant="caption" sx={{ ml: 1 }}>加载中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ py: 1 }}>
        <Typography variant="body2">权限检查失败: {error}</Typography>
        <Button 
          onClick={() => {
            // 重试按钮点击埋点
            trackingPrint({params:{
              type:"用户操作",
              title:"权限检查",
              subtitle:"重试按钮-点击",
              extra:{
                timestamp: Date.now(),
                previousError: error
              }
            }});
            fetchPermissions();
          }} 
          size="small" 
          sx={{ mt: 0.5 }}
        >
          重试
        </Button>
      </Alert>
    );
  }

  if (!permissionsData) {
    return (
      <Alert severity="info" sx={{ py: 1 }}>
        <Typography variant="body2">无权限数据，请重新加载</Typography>
      </Alert>
    );
  }

  const { permissions, deviceBrand, apiLevel } = permissionsData;

  return (
    <Paper elevation={1} sx={{ p: 1 }}>
      {/* 标题栏 */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center">
          <SecurityIcon sx={{ mr: 0.5, color: 'primary.main', fontSize: 18 }} />
          <Typography variant="body2" fontWeight="medium">权限状态</Typography>
        </Box>
        <Tooltip title="刷新">
          <IconButton 
            onClick={() => {
              // 刷新按钮点击埋点
              trackingPrint({params:{
                type:"用户操作",
                title:"权限检查",
                subtitle:"刷新按钮-点击",
                extra:{
                  timestamp: Date.now(),
                  currentPermissionsCount: permissionsData?.permissions.length || 0
                }
              }});
              fetchPermissions();
            }} 
            size="small" 
            sx={{ p: 0.5 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* 说明 */}
      <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
        <Typography variant="caption" display="block">
          🟢已授权 🔴未授权 🟡无法检测
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {deviceBrand} • API{apiLevel}
        </Typography>
      </Alert>

      {/* 权限列表 */}
      <List sx={{ py: 0 }}>
        {permissions.map((permission) => (
          <ListItem
            key={permission.id}
            sx={{
              borderLeft: 2,
              borderLeftColor: 
                permission.status === 'granted' ? 'success.main' :
                permission.status === 'denied' ? 'error.main' : 'warning.main',
              mb: 0.5,
              py: 0.5,
              px: 0.5,
              borderRadius: 0.5,
              bgcolor: 'grey.50',
              display: 'block' // 改为block布局
            }}
          >
            {/* 第一行：图标 + 名称 + 状态 + 信息按钮 */}
            <Box display="flex" alignItems="center" mb={0.5}>
              <Box sx={{ mr: 0.5 }}>
                {getStatusIcon(permission)}
              </Box>
              <Typography 
                variant="body2" 
                fontWeight="medium" 
                sx={{ flex: 1, minWidth: 0 }}
              >
                {permission.name}
              </Typography>
              <Tooltip title="查看权限说明">
                <IconButton
                  size="small"
                  onClick={() => handleShowPermissionInfo(permission)}
                  sx={{ mr: 0.5, p: 0.25 }}
                >
                  <InfoIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                </IconButton>
              </Tooltip>
              {getStatusChip(permission)}
            </Box>
            
            {/* 第二行：类型标签 + 确认信息 */}  
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Chip 
                  label={getTypeLabel(permission.type)} 
                  variant="outlined" 
                  size="small"
                  sx={{ 
                    fontSize: '0.65rem',
                    height: 18,
                    '& .MuiChip-label': { px: 0.5, py: 0 }
                  }}
                />
                {!permission.canDetect && (
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                    需确认
                  </Typography>
                )}
              </Box>
            </Box>
            
            {/* 第三行：操作按钮 */}
            <Box display="flex" gap={0.5}>
              {/* {(permission.id === 'lock_screen' || permission.id === 'lock_screen_popup' || permission.id === 'lock_screen_display') && permission.status === 'unknown' && (
                <Button
                  variant="outlined"
                  size="small" 
                  color="success"
                  onClick={handleConfirmLockScreenPermission}
                  sx={{ 
                    fontSize: '0.65rem',
                    minWidth: 'auto',
                    px: 0.5,
                    py: 0.25,
                    height: 24
                  }}
                >
                  已授权
                </Button>
              )} */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon sx={{ fontSize: 12 }} />}
                onClick={() => handleOpenSettings(permission)}
                sx={{ 
                  fontSize: '0.65rem',
                  minWidth: 'auto',
                  px: 0.5,
                  py: 0.25,
                  height: 24
                }}
              >
                设置
              </Button>
            </Box>
          </ListItem>
        ))}
      </List>

      {/* 底部时间戳 */}
      <Box mt={0.5} py={0.25} px={0.5} bgcolor="grey.100" borderRadius={0.25}>
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.6rem' }}>
          更新: {new Date(permissionsData.timestamp).toLocaleTimeString()}
        </Typography>
      </Box>

      {/* 权限说明对话框 */}
      <Dialog
        open={infoDialogOpen}
        onClose={handleCloseInfoDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { mx: 1 } // 移动端适配
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center">
            <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6" component="div">
                {selectedPermission?.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedPermission && getTypeLabel(selectedPermission.type)}权限
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 0 }}>
          {selectedPermission && (
            <Box>
              {/* 权限状态 */}
              <Box display="flex" alignItems="center" mb={2}>
                <Typography variant="subtitle2" sx={{ mr: 1 }}>当前状态:</Typography>
                <Box display="flex" alignItems="center">
                  {getStatusIcon(selectedPermission)}
                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                    {selectedPermission.status === 'granted' ? '已授权' : 
                     selectedPermission.status === 'denied' ? '未授权' : '待确认'}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* 权限用途说明 */}
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom color="primary.main">
                  🎯 为什么需要这个权限？
                </Typography>
                <Typography variant="body2" color="textSecondary" lineHeight={1.6}>
                  {getPermissionReason(selectedPermission)}
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* 影响说明 */}
              <Box mb={1}>
                <Typography variant="subtitle2" gutterBottom color="warning.main">
                  ⚡ 不授权的影响
                </Typography>
                <Typography variant="body2" color="textSecondary" lineHeight={1.6}>
                  {getPermissionImpact(selectedPermission)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              // 权限信息"我知道了"按钮埋点
              trackingPrint({params:{
                type:"用户操作",
                title:"权限信息",
                subtitle:"我知道了-点击",
                extra:{
                  timestamp: Date.now(),
                  permissionId: selectedPermission?.id,
                  permissionStatus: selectedPermission?.status
                }
              }});
              handleCloseInfoDialog();
            }} 
            color="primary"
          >
            我知道了
          </Button>
          {selectedPermission && (
            <Button
              variant="contained"
              onClick={() => {
                // 权限信息"去设置"按钮埋点
                trackingPrint({params:{
                  type:"用户操作",
                  title:"权限设置",
                  subtitle:"去设置-对话框点击",
                  extra:{
                    timestamp: Date.now(),
                    permissionId: selectedPermission.id,
                    permissionName: selectedPermission.name,
                    currentStatus: selectedPermission.status,
                    fromDialog: true
                  }
                }});
                
                handleOpenSettings(selectedPermission);
                handleCloseInfoDialog();
              }}
              color="primary"
            >
              去设置
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};